import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaSwitchComponent } from 'ra-ui';
import { QuoteItemEntity } from 'src/app/domain/entities/quotes/quote-item.entity';
import { QuoteItemTypeEnum } from 'src/app/domain/enums/quote-item-type.enum';
import { UnitOfMeasurementEnum } from 'src/app/domain/enums/unit-of-measurement.enum';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaInputComponent } from 'src/app/shared/components/ra-input/ra-input.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaSegmentedComponent } from 'src/app/shared/components/ra-segmented/ra-segmented.component';
import { RaSegmentedOption } from 'src/app/shared/components/ra-segmented/ra-segmented.model';
import {
    RaSelectModel,
    RaSelectOptionModel,
} from 'src/app/shared/components/ra-select/model/ra-select.model';
import { RaSelectComponent } from 'src/app/shared/components/ra-select/ra-select.component';
import { RaCurrencyPipe } from 'src/app/shared/pipes/ra-currency.pipe';
import { parseLocalizedNumberInput } from 'src/app/shared/utils/number-input.util';

/** Payload do modo criar — espelha o fluxo do app mobile (cria campo + opção de catálogo). */
export interface QuoteItemSaveNew {
    item: QuoteItemEntity;
    saveInCatalog: boolean;
}

/** Payload do modo editar — só os campos mutáveis (unidade não é editável). */
export interface QuoteItemSaveEdit {
    name: string;
    quantity: number;
    unitPrice: number;
    type: QuoteItemTypeEnum;
}

@Component({
    selector: 'modal-quote-item-form',
    templateUrl: 'modal-quote-item-form.component.html',
    styleUrl: 'modal-quote-item-form.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RaModalComponent,
        RaButtonComponent,
        RaInputComponent,
        RaSegmentedComponent,
        RaSelectComponent,
        RaSwitchComponent,
        RaCurrencyPipe,
        TranslatePipe,
    ],
})
export class ModalQuoteItemFormComponent {
    private _translate = inject(TranslateService);

    /** `null` = criar; preenchido = editar. */
    public item = input<QuoteItemEntity | null>(null);
    public loading = input<boolean>(false);
    public currencyCode = input<string | null | undefined>();
    public locale = input<string>('pt-BR');

    public close = output<void>();
    public saveNew = output<QuoteItemSaveNew>();
    public saveEdit = output<QuoteItemSaveEdit>();

    public isEdit = computed(() => !!this.item());

    public form = new FormGroup({
        name: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        // PRODUCT porque é a primeira opção do segmentado — o campo abre com a escolha
        // que está visualmente marcada.
        type: new FormControl<QuoteItemTypeEnum>(QuoteItemTypeEnum.PRODUCT, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        unitOfMeasurement: new FormControl<UnitOfMeasurementEnum>(UnitOfMeasurementEnum.UNIT, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        quantity: new FormControl<number>(1, {
            nonNullable: true,
            validators: [Validators.required, Validators.min(0.01)],
        }),
        // Nulo (não 0) para o campo nascer VAZIO: "R$ 0,00" pré-preenchido passa
        // despercebido e vira item sem preço.
        unitPrice: new FormControl<number | null>(null, {
            validators: [Validators.required, Validators.min(0)],
        }),
        saveInCatalog: new FormControl<boolean>(false, { nonNullable: true }),
    });

    /** Produto/Serviço são sempre estas duas: segmentado, não dropdown. */
    public typeOptions: RaSegmentedOption<QuoteItemTypeEnum>[];
    public unitSelectModel: RaSelectModel;

    constructor() {
        this.typeOptions = this._buildTypeOptions();
        this.unitSelectModel = this._buildUnitSelectModel();

        effect(() => {
            const current = this.item();
            const isEdit = !!current;

            if (current) {
                this.form.patchValue(
                    {
                        name: current.name ?? '',
                        type: current.type ?? QuoteItemTypeEnum.PRODUCT,
                        unitOfMeasurement: current.unitOfMeasurement ?? UnitOfMeasurementEnum.UNIT,
                        quantity: current.quantity ?? 1,
                        unitPrice: current.unitPrice ?? null,
                        saveInCatalog: false,
                    },
                    { emitEvent: false }
                );
            }

            this._updateUnitSelectSelected(this.form.controls.unitOfMeasurement.value);

            // Unidade não é editável no modo editar.
            this.unitSelectModel.disabled = isEdit;
        });
    }

    public onUnitSelect(option: RaSelectOptionModel): void {
        this.form.controls.unitOfMeasurement.setValue(option.value);
        this._updateUnitSelectSelected(option.value);
    }

    /**
     * Subtotal (quantidade × preço unitário) para o preview de moeda. Getter (não signal):
     * reavalia a cada change-detection conforme o usuário digita nos `ra-input`.
     */
    public get subtotal(): number {
        const raw = this.form.getRawValue();
        const quantity = Number(raw.quantity) || 0;
        const unitPrice = Number(raw.unitPrice) || 0;
        return quantity * unitPrice;
    }

    public onSave(): void {
        if (this.form.invalid) return;

        const raw = this.form.getRawValue();
        const quantity = parseLocalizedNumberInput(raw.quantity) ?? 0;
        const unitPrice = parseLocalizedNumberInput(raw.unitPrice) ?? 0;

        if (this.isEdit()) {
            this.saveEdit.emit({
                name: raw.name,
                quantity,
                unitPrice,
                type: raw.type,
            });
            return;
        }

        const item = new QuoteItemEntity();
        item.name = raw.name;
        item.type = raw.type;
        item.unitOfMeasurement = raw.unitOfMeasurement;
        item.quantity = quantity;
        item.unitPrice = unitPrice;

        this.saveNew.emit({ item, saveInCatalog: raw.saveInCatalog });
    }

    private _buildTypeOptions(): RaSegmentedOption<QuoteItemTypeEnum>[] {
        const tr = this._translate;
        return [
            {
                label: tr.instant('document.edit.quote.type.PRODUCT'),
                value: QuoteItemTypeEnum.PRODUCT,
            },
            {
                label: tr.instant('document.edit.quote.type.SERVICE'),
                value: QuoteItemTypeEnum.SERVICE,
            },
        ];
    }

    private _buildUnitSelectModel(): RaSelectModel {
        const tr = this._translate;
        const model = new RaSelectModel();
        model.placeholder = tr.instant('document.edit.quote.itemModal.unit');
        model.loading = false;
        model.disabled = false;
        model.listOptions = [
            UnitOfMeasurementEnum.UNIT,
            UnitOfMeasurementEnum.HOUR,
            UnitOfMeasurementEnum.METER,
            UnitOfMeasurementEnum.SQUARE_METERS,
            UnitOfMeasurementEnum.CUBIC_METERS,
        ].map(unit => ({
            description: tr.instant(`document.edit.quote.units.${unit}`),
            value: unit,
            selected: false,
        }));
        return model;
    }

    private _updateUnitSelectSelected(unit: UnitOfMeasurementEnum): void {
        this.unitSelectModel.listOptions.forEach(opt => {
            opt.selected = opt.value === unit;
        });
    }
}
